<script setup lang="ts">
  import FlowPathInput from '@/components/form/FlowPathInput.vue'
  import {
    FormFieldContainer,
    InputField,
    Reorder,
    SwitchButton,
    UiButton,
    type ButtonListElement,
  } from '@logistiek-zonder-papier/lzp-library'
  import { reactive } from 'vue'

  const field = defineModel<ButtonListElement>({ required: true })

  const touched = reactive({
    storeLocation: false,
  })

  function validateRequired(key: 'storeLocation', message: string) {
    return touched[key] && field.value?.type === 'buttonList' && !field.value?.[key]?.trim()
      ? message
      : null
  }

  function addButton() {
    field.value.buttons.push({ label: '', value: '' })
  }

  function deleteButton(index: number) {
    field.value.buttons.splice(index, 1)
  }
</script>

<template>
  <div class="field-config-container">
    <FormFieldContainer label="Label">
      <FlowPathInput
        placeholder="e.g. ${customer.name}"
        hint="Field label. Supports ${...} interpolation."
        v-model="field.label"
        :optional="true"
        type="text"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer
      label="Store Location"
      :error="validateRequired('storeLocation', 'Store Location is required')"
    >
      <InputField
        placeholder="x.path.to.location"
        hint="Where the value of the pressed button is stored."
        v-model="field.storeLocation"
        type="text"
        @blur="touched.storeLocation = true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Buttons">
      <template #action-button>
        <UiButton
          label="Add button"
          variant="prominent"
          size="small"
          icon="plus"
          @click="addButton"
        />
      </template>
      <div class="repeatable">
        <div v-for="(button, index) in field.buttons" :key="index" class="section">
          <FormFieldContainer :label="`Button ${index + 1}`">
            <template #action-button>
              <Reorder v-model="field.buttons" :index="index" />
              <UiButton
                icon="delete"
                size="small"
                variant="negative"
                @click="deleteButton(index)"
              />
            </template>
            <FlowPathInput
              label="Label"
              placeholder="Literal label or ${xpathvalue}"
              v-model="button.label"
              :optional="false"
              type="text"
              :validate-paths="true"
            />
            <FormFieldContainer label="Value">
              <InputField
                placeholder="Value written to the store location"
                v-model="button.value"
                type="text"
              />
            </FormFieldContainer>
          </FormFieldContainer>
        </div>
      </div>
    </FormFieldContainer>

    <FormFieldContainer label="Required">
      <SwitchButton v-model="field.required" />
    </FormFieldContainer>

    <FormFieldContainer label="Error message">
      <FlowPathInput
        placeholder="Error message under the field"
        v-model="field.error"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>
  </div>
</template>

<style scoped>
  .field-config-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .repeatable {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
  }

  .section {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-xs);
    border-radius: var(--components-form-field-corners);
    border: 1px solid var(--colours-grey-3);
    box-shadow: 1px 1px 1px 1px var(--colours-grey-3);
  }
</style>
